import {Component, ChangeDetectorRef, OnInit} from '@angular/core';
import * as _ from 'lodash';

import { BaseScorecardComponent } from '../base/_base-scorecard.component';
import { SegmentFieldItem } from 'utils/segment-field-item.interface';
import { ScorecardItemComponent } from 'shared/scorecard/scorecard-item/scorecard-item.component';

import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';
import {takeWhile} from 'rxjs/operators';


@Component({
  template: '',
})
export class BaseScorecardListComponent extends BaseScorecardComponent implements OnInit {
  segmentTitle: string = '';
  segmentName: string = '';
  pathToScorecard = this.segmentName;
  pathToScorecardTitle = '';
  declare segmentData: any;

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get segmentScores(): any {
   return _.get(this.scoreData, `segments.${this.segmentName}.tallies`);
  }

  get itemDetailData(): Array<any> {
    return _.get(this.itemDetail, this.segmentName);
  }

  get isMissingItem(): boolean {
    return _.has(this.scoreData, `segments.${this.segmentName}.non-standard.missing`);
  }

  get missingScore(): any {
    return _.get(this.scoreData, `segments.${this.segmentName}.non-standard.missing`);
  }

  get hasScores(): boolean {
    return !!this.segmentScores;
  }

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  set itemDetailData(data: Array<any>) {
    _.set(this.itemDetail, this.segmentName, data);
  }

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
  ) {
    super(ref, scorecardConnectorService);
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    super.ngOnInit();

    // subscribe on row expanded
    this.scorecardConnectorService.tableRowCollapsed
    .pipe(takeWhile(() => this.isActive))
    .subscribe((data: any) => {
      // {index: number, collapsed: boolean}
      if (this.scorecardItems && this.scorecardItems.toArray().length) {
        const scorecardItem = this.scorecardItems.toArray()[data.index];

        if (scorecardItem) {
          scorecardItem.collapseDetails(data.collapsed);
          this.ref.markForCheck();
        }
      }
    });

    if (this.isMissingItem && !_.size(this.segmentScores)) {
      // at least one score must always be when attribute missing
      this.missingScore.valid = 0;
      this.missingScore.populated = 0;
      this.addNewScore();
    }
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  /*
    * Replaced parent
    * $event: {status: string, item: SegmentFieldItem, index: number, valueIndex?: number|null}
   */
  protected getItemFieldScoreFromEvent($event: any) {
    if (_.isNil($event.valueIndex)) {
      return this.getScoreItem($event.item, $event.index);
    } else {
      return this.getScoreIndexItem($event.item, $event.index, $event.valueIndex);
    }
  }

  /*
    * Replaced parent
    * $event: {item: SegmentFieldItem, index: number, valueIndex?: number}
   */
  protected handleControlFocus($event: any) {
    if (this.hasScores) {
      this.handleNestedControlFocus($event);
    }
  }

  protected findScorecardItemViaData(data: any): ScorecardItemComponent {
    if (!this.hasScores) { return null; }
    return this.scorecardItems.toArray()[data.index];
  }

  protected collapseChildScoresViaData(scorecardItem: ScorecardItemComponent, data: any) {
    if (this.hasScores) {
      this.collapseExpandedChildScores(scorecardItem.scorecardChildItems, data.item.field_map);
    }
  }

  protected getChildScorecardItemViaData(scorecardItem: ScorecardItemComponent, data: any): ScorecardItemComponent {
    if (!this.hasScores) { return null; }
    return this.getChildScorecardItemByName(scorecardItem, data.item.field_map);
  }

  protected handleControlAdd() {
    if (this.hasScores) {
      this.addNewScore();
    }
  }

  /*
    * $event: {index?: number, valueIndex?: number}
   */
  protected handleControlRemove($event: any) {
    if (!_.isNil($event.valueIndex)) {
      const score: any = _.get(_.nth(this.segmentScores, $event.index), 'value');
      _.pullAt(score, [$event.valueIndex]);
      this.ref.markForCheck();
    }
  }

  // replace parent method
  protected createNewScore() {
    const item = {};
    _.forEach(this.segmentData, (segment: any) => {
      // check if it is in scoreData
      if (this.getScorecardField(segment.item_name)) {
        _.set(item, segment.item_name, this.getEmptyScoreArrayItem(segment.item_name));
      }
    });
    return item;
  }

  protected getScorecardTitle(item: any) {
    const title = _.get(item, this.pathToScorecardTitle);
    return title ? title : '';
  }

  protected getScoreItem(item: SegmentFieldItem, index: number|string): any {
    return item ? _.get(this.segmentScores, `${index}.${item.item_name}`) : null;
  }

  /*
    * Expands nested score details according on selected item
    * Common handler for expanding nested scores
  */
  protected handleNestedControlFocus($event: any) {
    const scorecardItem = this.findScorecardItemViaData($event);
    // collapse all scorecads, which are expanded now
    this.collapseExpandedScores($event.item.field_map);
    // collapse child scorecards of the active scorecard item (languages ['en', 'es', 'fr'])
    this.collapseChildScoresViaData(scorecardItem, $event);
    _.defer(() => {
      // expand active scorecard item
      if (_.get(scorecardItem, 'collapsed')) { scorecardItem.collapseDetails(false); }
      // expand active child scorecard item (language in case of description segment)
      const childScorecardItem = this.getChildScorecardItemViaData(scorecardItem, $event);
      if (_.get(childScorecardItem, 'collapsed')) {
        childScorecardItem.collapseDetails(false);
        // scroll to active scorecard child item
        _.delay(() => {
          if (this.scorecard) {
            this.scorecard.scrollToScorecardItem(childScorecardItem);
          }
          this.ref.markForCheck();
        }, 500);
      }

      this.ref.markForCheck();
    });
  }

  protected getScorecardField(name: string): any {
    return _.find(this.scorecardFields, (scorecardField: any) => {
      return (scorecardField.path.indexOf(`${this.segmentName}.%i.${name}`) === 0);
    });
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  /*
    * Checks item's high level validity
  */
  private isItemValid(item: any) {
    if (_.isArray(item)) {
      /*
        if it's array of fields - recursive call for each
      */
      return _.every(item, (scoreField: any) => {
        return this.isItemValid(scoreField);
      });
    } else {
      // if it's object
      if (_.has(item, 'valid') || _.has(item, 'validation_rule') ) {
        /*
          if it's simple field
          {
            path: string, populated: number, possible: number, rule: any,
            score: number, valid: number,
          } - return valid
        */
        return item.valid === 1;
      } else {
        /*
          it's nested field like description: {language: any, type: any, value: any}
          - call validation for each
        */
        return _.every(_.mapValues(item, value => this.isItemValid(value)), i => i);
      }
    }
  }

  private getItemData(index: number|string): any {
    return _.get(this.itemDetail, `${this.segmentName}[${index}]`);
  }

  private getScoreIndexItem(item: SegmentFieldItem, index: number|string, propertyIndex: number): any {
    return item ? _.get(this.segmentScores, `${index}.${item.item_name}[${propertyIndex}]`) : null;
  }

  private getItemValidationMessage(item: any, attribute: any, index: number, valueIndex?: number): string {
    const score = this.getItemFieldScoreFromEvent({item, index, valueIndex});
    if (!score || score.valid) { return ''; }
    return this.getValidationMessage(item, score);
  }

  private addNewScore(): void {
    if (this.segmentScores) {
      this.segmentScores.push(this.createNewScore());
      this.ref.markForCheck();
    }
  }
}
